import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_BASE } from './apiConfig';
import { Button } from './components/ui';
import AuthLayout, { AuthField, AuthMessage, EMAIL_RE, readJson } from './components/auth/AuthLayout';

const COOLDOWN = 60; // seconds before another link can be requested (the server allows 5 per 15 min)

const fmtWait = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

function sendProblem(status, data) {
    const message = data.message || data.error || '';
    if (status === 429 || /too many/i.test(message)) return 'Too many requests. Please try again later.';
    if (/already verified/i.test(message)) return { verified: true };
    if (/invalid email/i.test(message)) return 'No account uses this email. Check the address, or create an account.';
    return message || 'We couldn’t send the email. Please try again.';
}

/**
 * /verify-email — "Check your email". Arrives with router state {email, sent, from}:
 * from sign-up (link just sent → cooldown), from log in / a failed link (offer a new link),
 * or with nothing (direct visit → ask for the address). Resend uses the same
 * POST /auth/verify-email/send-email the original portal used.
 */
function VerifyEmailPage() {
    const { state } = useLocation();
    const [email, setEmail] = useState(state?.email || '');
    const [known, setKnown] = useState(Boolean(state?.email));
    const [sent, setSent] = useState(state?.sent === true);
    const [wait, setWait] = useState(state?.sent === true ? COOLDOWN : 0);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(state?.sent === false ? { tone: 'danger', text: 'Your account was created, but we couldn’t send the verification email. Send it again below.' } : null);
    const [alreadyVerified, setAlreadyVerified] = useState(false);
    const [tried, setTried] = useState(false);
    const emailRef = useRef(null);

    useEffect(() => {
        if (wait <= 0) return undefined;
        const t = setTimeout(() => setWait((w) => w - 1), 1000);
        return () => clearTimeout(t);
    }, [wait]);

    const address = email.trim();
    const emailErr = !EMAIL_RE.test(address) ? 'Enter the email you signed up with.' : '';

    async function send(e) {
        e?.preventDefault();
        if (busy || wait > 0) return;
        if (emailErr) {
            setTried(true);
            emailRef.current?.focus();
            return;
        }
        setBusy(true);
        setNotice(null);
        try {
            const res = await fetch(`${API_BASE}/auth/verify-email/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: address }),
            });
            if (res.ok) {
                setKnown(true);
                setSent(true);
                setWait(COOLDOWN);
                setNotice({ tone: 'success', text: `We sent a new link to ${address}. It replaces any earlier link.` });
            } else {
                const p = sendProblem(res.status, await readJson(res));
                if (p?.verified) setAlreadyVerified(true);
                else setNotice({ tone: 'danger', text: p });
            }
        } catch {
            setNotice({ tone: 'danger', text: 'We couldn’t reach enAble. Check your connection and try again.' });
        } finally {
            setBusy(false);
        }
    }

    if (alreadyVerified) {
        return (
            <AuthLayout>
                <AuthMessage
                    tone="success"
                    icon="check"
                    kicker="Verified"
                    title="This email is already verified"
                    body={address}
                    links={null}
                >
                    <Button variant="primary" size="lg" block to="/login">Continue to log in</Button>
                </AuthMessage>
            </AuthLayout>
        );
    }

    const body = sent ? `Sent to ${address}` : known ? address : '';
    const label = busy
        ? 'Sending…'
        : wait > 0
            ? `Resend in ${fmtWait(wait)}`
            : sent ? 'Resend email' : 'Send verification link';

    return (
        <AuthLayout>
            <AuthMessage
                tone="accent"
                icon="mail"
                kicker="Almost there"
                title="Check your email"
                body={body}
                links={(
                    <>
                        {state?.from === 'signup' ? <Link className="au-link" to="/sign-up">Wrong address? Change email</Link> : null}
                        <Link className="au-link" to="/login">Back to log in</Link>
                    </>
                )}
            >
                <form className="au-form" onSubmit={send} noValidate>
                    {!known ? (
                        <AuthField
                            label="Email"
                            type="email"
                            name="email"
                            autoComplete="email"
                            inputMode="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={tried ? emailErr : ''}
                            inputRef={emailRef}
                        />
                    ) : null}
                    <Button type="submit" variant="primary" size="lg" block disabled={wait > 0} aria-disabled={busy || undefined}>
                        {label}
                    </Button>
                </form>
                {notice ? (
                    <span role={notice.tone === 'danger' ? 'alert' : 'status'} className={`au-note au-note--${notice.tone}`}>{notice.text}</span>
                ) : null}
            </AuthMessage>
        </AuthLayout>
    );
}

export default VerifyEmailPage;
